
import React, { useState, useEffect } from 'react';
import { StationAssignment, Employee, UserStatus } from '../types';
import AssignEmployeeModal from './AssignEmployeeModal'; 

interface StationRowProps {
  stationAssignment: StationAssignment;
  allEmployees: Employee[];
  onUpdateAssignment: (updatedAssignment: StationAssignment) => void;
  stationNumberDisplayName: string;
  allLineStationAssignments: StationAssignment[]; // All assignments for current line and shift
  lineMode?: string; // Current line mode
  currentShift?: number | null; // Current active shift (1, 2, or null if outside shift hours)
  currentLineId?: string; // Current line ID for the assignment modal
}

const StationRow: React.FC<StationRowProps> = ({
  stationAssignment,
  allEmployees,
  onUpdateAssignment,
  stationNumberDisplayName,
  allLineStationAssignments,
  lineMode = 'stop',
  currentShift = null,
  currentLineId,
}) => {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  useEffect(() => {
    console.log(`StationRow (${stationNumberDisplayName}): Props received/updated. Assigned ID: '${stationAssignment.assignedEmployeeId}', Status: ${stationAssignment.status}, AllEmployees: ${allEmployees?.length}`);
    // Check if employees have currentAssignment property
    const employeesWithAssignments = allEmployees?.filter(e => e.currentAssignment) || [];
    console.log(`StationRow (${stationNumberDisplayName}): Employees with currentAssignment property: ${employeesWithAssignments.length}`);
    if (employeesWithAssignments.length > 0) {
      console.log(`StationRow (${stationNumberDisplayName}): Example employee with assignment:`, employeesWithAssignments[0]);
    }
  }, [stationAssignment, allEmployees, stationNumberDisplayName]);

  // Filter out employees who are already assigned to other stations on this line and shift
  const availableEmployees = allEmployees.filter(employee => {
    // Get all employee IDs that are assigned to OTHER stations (not this one)
    const assignedEmployeeIds = allLineStationAssignments
      .filter(assignment => assignment.id !== stationAssignment.id) // Exclude current station
      .map(assignment => assignment.assignedEmployeeId)
      .filter(id => id !== null); // Remove null values
    
    console.log(`StationRow (${stationNumberDisplayName}): Already assigned employee IDs on this line/shift:`, assignedEmployeeIds);
    
    // Include employee if they're not assigned to any other station on this line/shift
    const isAvailable = !assignedEmployeeIds.includes(employee.id);
    
    if (!isAvailable) {
      console.log(`StationRow (${stationNumberDisplayName}): Filtering out employee ${employee.firstName} ${employee.lastName} (${employee.id}) - already assigned`);
    }
    
    return isAvailable;
  });
  
  console.log(`StationRow (${stationNumberDisplayName}): Available employees: ${availableEmployees.length}/${allEmployees.length}`);

  const handleEmployeeStatusToggle = () => {
    // Don't allow setting employee to "Working" if station is disabled
    if (stationAssignment.stationStatus === 'disabled') {
      console.log('Cannot set employee to Working when station is disabled');
      return;
    }
    
    // VALIDATION: Don't allow setting to "Working" if no employee is assigned
    if (!stationAssignment.assignedEmployeeId && stationAssignment.status === UserStatus.Inactive) {
      console.log('Cannot set station to Working without an assigned employee');
      alert('Please assign an employee before setting the station to Working status.');
      return;
    }
    
    // VALIDATION: Don't allow setting to "Working" outside of shift hours
    if (stationAssignment.status === UserStatus.Inactive && currentShift === null) {
      console.log('Cannot set station to Working outside of shift hours');
      alert('Cannot set station to Working outside of shift hours. Shifts run from 6:00 AM - 2:00 PM (Shift 1) and 2:00 PM - 3:00 AM (Shift 2).');
      return;
    }
    
    // VALIDATION: Don't allow setting to "Working" if station is assigned to different shift than current
    const stationShift = stationAssignment.shift === 'SHIFT_1' ? 1 : 
                         stationAssignment.shift === 'SHIFT_2' ? 2 : null;
    if (stationAssignment.status === UserStatus.Inactive && 
        stationShift !== null && 
        currentShift !== null && 
        stationShift !== currentShift) {
      console.log(`Cannot set station to Working - station is for shift ${stationShift} but current shift is ${currentShift}`);
      alert(`This station is assigned to Shift ${stationShift}, but the current active shift is Shift ${currentShift}. You can only activate stations for the current shift.`);
      return;
    }
    
    // VALIDATION: Don't allow setting to "Working" if line is in stop, prepare, or clean mode
    if (stationAssignment.status === UserStatus.Inactive && 
        (lineMode === 'stop' || lineMode === 'prepare' || lineMode === 'clean')) {
      console.log(`Cannot set station to Working - line is in ${lineMode} mode`);
      const modeMessage = lineMode === 'stop' ? 'Stop mode' : 
                         lineMode === 'prepare' ? 'Prepare mode' : 
                         'Clean mode';
      alert(`Cannot set station to Working while this line is in ${modeMessage}. Please switch this line to Production or Production Standby mode first.`);
      return;
    }
    
    const newStatus = stationAssignment.status === UserStatus.Active ? UserStatus.Inactive : UserStatus.Active;
    console.log(`StationRow: Toggling worker status from ${stationAssignment.status} to ${newStatus} for station ${stationNumberDisplayName}`);
    onUpdateAssignment({ ...stationAssignment, status: newStatus });
  };
  
  const handleStationStatusToggle = () => {
    const newStationStatus = stationAssignment.stationStatus === 'disabled' ? 'active' : 'disabled';
    
    // If disabling the station, unassign any employee and set them to not working
    if (newStationStatus === 'disabled') {
      onUpdateAssignment({ 
        ...stationAssignment, 
        assignedEmployeeId: null, // Unassign the employee
        status: UserStatus.Inactive, // Set to "Not Working"
        stationStatus: newStationStatus 
      });
    } else {
      // When enabling the station, just change the station status
      onUpdateAssignment({ ...stationAssignment, stationStatus: newStationStatus });
    }
  };

  const handleAssignEmployee = (employeeId: string | null) => {
    onUpdateAssignment({ ...stationAssignment, assignedEmployeeId: employeeId });
    setIsAssignModalOpen(false);
  };

  const getStatusButtonClass = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Active:
        return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0 shadow-md hover:shadow-lg hover:from-green-600 hover:to-emerald-600 transform hover:scale-105';
      case UserStatus.Inactive:
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white border-0 shadow-md hover:shadow-lg hover:from-gray-500 hover:to-gray-600 transform hover:scale-105';
      default:
        return 'bg-gradient-to-r from-slate-400 to-slate-500 text-white border-0 shadow-md hover:shadow-lg';
    }
  };

  const getStatusDisplayText = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Active:
        return '✓ Working';
      case UserStatus.Inactive:
        return '× Not Working';
      default:
        return 'Unknown';
    }
  };
  
  let employeeDisplayNode: React.ReactNode;
  let foundEmployeeObj: Employee | undefined = undefined;

  if (!stationAssignment.assignedEmployeeId) {
    employeeDisplayNode = <span style={{ color: 'gray', fontStyle: 'italic' }}>Unassigned</span>;
  } else {
    if (!allEmployees || !Array.isArray(allEmployees) || allEmployees.length === 0) {
      employeeDisplayNode = <span style={{ color: 'red', fontStyle: 'italic' }}>Employee List Missing/Empty</span>;
    } else {
      foundEmployeeObj = allEmployees.find(emp => emp.id === stationAssignment.assignedEmployeeId);
      if (foundEmployeeObj) {
        const firstName = foundEmployeeObj.firstName?.trim();
        const lastName = foundEmployeeObj.lastName?.trim();
        const empId = foundEmployeeObj.employeeId?.trim();
        
        let nameStr = '';
        if (firstName && lastName) nameStr = `${firstName} ${lastName}`;
        else if (firstName) nameStr = firstName;
        else if (lastName) nameStr = lastName;

        let idStr = empId ? `(${empId})` : '';
        let displayText = '';

        if (nameStr && idStr) displayText = `${nameStr} ${idStr}`;
        else if (nameStr) displayText = nameStr;
        else if (idStr) displayText = idStr;
        
        if (displayText.trim()) {
          employeeDisplayNode = displayText;
        } else {
          employeeDisplayNode = <span style={{ color: 'orange', fontStyle: 'italic' }}>Incomplete Emp. Data</span>;
        }
      } else {
        employeeDisplayNode = <span style={{ color: 'darkred', fontStyle: 'italic' }}>Emp. ID Not Found</span>;
      }
    }
  }
  
  const isEffectivelyBlank = !employeeDisplayNode || (typeof employeeDisplayNode === 'string' && !employeeDisplayNode.trim());

  console.log(`StationRow (${stationNumberDisplayName}): Assigned ID: '${stationAssignment.assignedEmployeeId}', Found: ${!!foundEmployeeObj}, Display Node:`, employeeDisplayNode, `Is Blank: ${isEffectivelyBlank}`);


  const isStationDisabled = stationAssignment.stationStatus === 'disabled';
  
  return (
    <>
      <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 items-center p-4 rounded-lg mb-2 transition-all duration-300 ${
        isStationDisabled 
          ? 'bg-gradient-to-r from-gray-100 to-gray-50 opacity-60 border-2 border-gray-300' 
          : 'bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 border-2 border-transparent hover:border-indigo-200 shadow-sm hover:shadow-md'
      }`}>
        <div className="flex items-center space-x-2">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-md ${
            isStationDisabled ? 'bg-gray-400' : 'bg-gradient-to-br from-indigo-500 to-purple-600'
          }`}>
            {stationAssignment.stationNumber}
          </div>
          <span className="font-semibold text-slate-800">{stationNumberDisplayName}</span>
        </div>
        
        <div
          onClick={() => !isStationDisabled && setIsAssignModalOpen(true)}
          className={`w-full p-3 rounded-lg shadow-sm text-left transition-all duration-200 flex items-center ${
            isStationDisabled 
              ? 'bg-gray-50 cursor-not-allowed border-2 border-gray-200' 
              : 'bg-white border-2 border-slate-200 cursor-pointer hover:border-indigo-400 hover:shadow-md hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500 focus:outline-none'
          }`}
          role="button"
          tabIndex={isStationDisabled ? -1 : 0}
          onKeyDown={(e) => { if (!isStationDisabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setIsAssignModalOpen(true); }}}
          aria-label={`Assign employee for ${stationNumberDisplayName}. Current: ${typeof employeeDisplayNode === 'string' ? employeeDisplayNode : 'Assigned status'}`}
          aria-disabled={isStationDisabled}
        >
          <div className="flex-1">
            {isEffectivelyBlank ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Display Issue
              </span>
            ) : (
              <div className="flex items-center">
                {stationAssignment.assignedEmployeeId ? (
                  <>
                    <svg className="w-5 h-5 mr-2 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium text-slate-700">{employeeDisplayNode}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-500 italic">{employeeDisplayNode}</span>
                  </>
                )}
              </div>
            )}
          </div>
          {!isStationDisabled && (
            <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={handleEmployeeStatusToggle}
            disabled={isStationDisabled || !stationAssignment.assignedEmployeeId}
            className={`w-full px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
              isStationDisabled || !stationAssignment.assignedEmployeeId
                ? 'bg-gray-100 text-gray-400 border-2 border-gray-200 cursor-not-allowed' 
                : getStatusButtonClass(stationAssignment.status)
            }`}
            aria-pressed={stationAssignment.status === UserStatus.Active}
            aria-label={`Set employee status for ${stationNumberDisplayName} to ${stationAssignment.status === UserStatus.Active ? 'Not Working' : 'Working'}`}
            title={!stationAssignment.assignedEmployeeId ? 'Assign an employee first' : ''}
          >
            {stationAssignment.status === UserStatus.Active && !isStationDisabled && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-200 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-300"></span>
              </span>
            )}
            <span>{getStatusDisplayText(stationAssignment.status)}</span>
          </button>
        </div>
        
        <div>
          <button
            type="button"
            onClick={handleStationStatusToggle}
            className={`relative w-full px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
              isStationDisabled
                ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md hover:shadow-lg hover:from-red-600 hover:to-red-700 transform hover:scale-105'
                : 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md hover:shadow-lg hover:from-emerald-600 hover:to-green-700 transform hover:scale-105'
            }`}
            aria-pressed={!isStationDisabled}
            aria-label={`Set station ${stationNumberDisplayName} to ${isStationDisabled ? 'Active' : 'Disabled'}`}
          >
            {!isStationDisabled && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
            )}
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              {isStationDisabled ? (
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              )}
            </svg>
            <span>Station {isStationDisabled ? 'Disabled' : 'Active'}</span>
          </button>
        </div>
      </div>

      <AssignEmployeeModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        allEmployees={allEmployees}
        currentAssignedEmployeeId={stationAssignment.assignedEmployeeId}
        onAssign={handleAssignEmployee}
        stationNumberDisplayName={stationNumberDisplayName}
        currentLineId={currentLineId || stationAssignment.lineId}
        currentShift={stationAssignment.shift}
      />
    </>
  );
};

export default StationRow;
